import { useCallback, useEffect, useRef, useState } from "react";
import { GameWorld } from "./GameWorld.tsx";
import { useGameAudio } from "./gameAudio.ts";
import { ORBIT_TARGETS, TARGET_DWELL_MS, targetReached } from "../shared/game.ts";
import { playablePoint } from "../shared/premium-game.ts";
import { constrainPointToOrbit, type CalibratedPoint } from "../shared/tracking.ts";
import { trackEvent } from "./analytics.tsx";
import { beginActivity, finishActivity, syncAccountActivity, type ActiveActivity } from "./activity.ts";
import { GentleGameArena, GentleGameIntro, GentleGameResult } from "./GentleGameFrame.tsx";
import { useLocale } from "./i18n.tsx";
import type { Session } from "./Shell.tsx";
import type { GentleGameRuntime } from "./useGentleGame.ts";
import { useHeadInput } from "./useHeadInput.ts";

type Stage = "intro" | "starting" | "calibrating" | "countdown" | "playing" | "complete";
type ControlMode = "camera" | "manual";

const ZERO_POINT = { x: 0, y: 0 };

export function GamePage({ session }: { session: Session | null }) {
  const { copy } = useLocale();
  const text = copy.game;
  const audio = useGameAudio("firefly");
  const {
    videoRef,
    status: headInputStatus,
    input: headInput,
    error: trackerError,
    loadProgress,
    calibrationProgress,
    start: startTracker,
    stop: stopTracker,
  } = useHeadInput();
  const [stage, setStage] = useState<Stage>("intro");
  const [mode, setMode] = useState<ControlMode>("camera");
  const [countdown, setCountdown] = useState(3);
  const [targetIndex, setTargetIndex] = useState(0);
  const [dwell, setDwell] = useState(0);
  const [manualPoint, setManualPoint] = useState<CalibratedPoint>(ZERO_POINT);
  const [elapsed, setElapsed] = useState(0);
  const pointRef = useRef<CalibratedPoint | null>(null);
  const gameStarted = useRef(0);
  const permissionTracked = useRef(false);
  const activityRef = useRef<ActiveActivity | null>(null);

  const cameraPoint = playablePoint(headInput);
  const cameraCursor = headInput ? { x: headInput.x, y: headInput.y } : null;
  const point = mode === "manual" ? manualPoint : cameraPoint;
  pointRef.current = point;

  const reset = useCallback(() => {
    if (activityRef.current) {
      finishActivity(activityRef.current, "stopped");
      if (session) void syncAccountActivity().catch(() => {});
      activityRef.current = null;
    }
    stopTracker();
    audio.stop();
    setStage("intro");
    setTargetIndex(0);
    setDwell(0);
    setElapsed(0);
    setManualPoint(ZERO_POINT);
  }, [audio.stop, session, stopTracker]);

  const startCamera = useCallback(async () => {
    audio.unlock();
    setMode("camera");
    setStage("starting");
    setTargetIndex(0);
    permissionTracked.current = false;
    await startTracker();
  }, [audio.unlock, startTracker]);

  const startManual = useCallback(() => {
    audio.unlock();
    stopTracker();
    setMode("manual");
    setTargetIndex(0);
    setDwell(0);
    setManualPoint(ZERO_POINT);
    setCountdown(3);
    setStage("countdown");
  }, [audio.unlock, stopTracker]);

  useEffect(() => {
    if (stage !== "starting") return;
    if (["loading", "face-lost", "calibrating", "ready"].includes(headInputStatus) && !permissionTracked.current) {
      permissionTracked.current = true;
      void trackEvent("camera_permission_outcome", { outcome: "granted" });
    }
    if (headInputStatus === "calibrating") {
      setStage("calibrating");
    } else if (headInputStatus === "error" && !permissionTracked.current) {
      permissionTracked.current = true;
      void trackEvent("camera_permission_outcome", { outcome: "unavailable" });
    }
  }, [headInputStatus, stage]);

  useEffect(() => {
    if (stage !== "calibrating" || headInputStatus !== "ready") return;
    setCountdown(3);
    setStage("countdown");
    void trackEvent("calibration_completed", { mode: "camera" });
  }, [headInputStatus, stage]);

  useEffect(() => {
    if (stage !== "countdown") return;
    if (countdown <= 0) {
      gameStarted.current = performance.now();
      activityRef.current = beginActivity();
      setStage("playing");
      audio.play("start");
      void trackEvent("game_started", { game: "firefly_garden", mode });
      return;
    }
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), 700);
    return () => window.clearTimeout(timer);
  }, [audio.play, countdown, mode, stage]);

  const complete = useCallback(() => {
    const seconds = Math.max(1, Math.round((performance.now() - gameStarted.current) / 1_000));
    if (activityRef.current) {
      finishActivity(activityRef.current, "completed");
      if (session) void syncAccountActivity().catch(() => {});
      activityRef.current = null;
    }
    setElapsed(seconds);
    setStage("complete");
    audio.play("complete");
    stopTracker();
    void trackEvent("game_completed", { game: "firefly_garden", mode, targets: ORBIT_TARGETS.length, duration_seconds: seconds });
  }, [audio.play, mode, session, stopTracker]);

  useEffect(() => {
    const finishInterruptedActivity = () => {
      if (!activityRef.current) return;
      finishActivity(activityRef.current, "stopped");
      activityRef.current = null;
    };
    window.addEventListener("pagehide", finishInterruptedActivity);
    return () => {
      window.removeEventListener("pagehide", finishInterruptedActivity);
      finishInterruptedActivity();
    };
  }, []);

  useEffect(() => {
    if (stage !== "playing") return;
    let animation = 0;
    let last = performance.now();
    let accumulated = 0;
    let lastPainted = -1;
    const target = ORBIT_TARGETS[targetIndex];
    const tick = (now: number) => {
      const delta = Math.min(100, now - last);
      last = now;
      if (target && pointRef.current && targetReached(pointRef.current, target)) accumulated += delta;
      else accumulated = 0;
      const progress = Math.min(1, accumulated / TARGET_DWELL_MS);
      if (Math.abs(progress - lastPainted) >= 0.025 || progress === 0 || progress === 1) {
        lastPainted = progress;
        setDwell(progress);
      }
      setElapsed(Math.max(0, Math.floor((now - gameStarted.current) / 1_000)));
      if (accumulated >= TARGET_DWELL_MS) {
        if (targetIndex >= ORBIT_TARGETS.length - 1) complete();
        else {
          audio.play("arrive");
          setTargetIndex((value) => value + 1);
          setDwell(0);
        }
        return;
      }
      animation = requestAnimationFrame(tick);
    };
    animation = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animation);
  }, [audio.play, complete, stage, targetIndex]);

  const moveManualPoint = useCallback((clientX: number, clientY: number, bounds: DOMRect) => {
    setManualPoint(constrainPointToOrbit({
      x: ((clientX - bounds.left) / bounds.width) * 2 - 1,
      y: ((clientY - bounds.top) / bounds.height) * 2 - 1,
    }));
  }, []);

  const moveManualByKey = useCallback((key: string) => {
    const delta = 0.12;
    setManualPoint((current) => {
      const changes: Record<string, CalibratedPoint> = {
        ArrowLeft: { x: current.x - delta, y: current.y },
        ArrowRight: { x: current.x + delta, y: current.y },
        ArrowUp: { x: current.x, y: current.y - delta },
        ArrowDown: { x: current.x, y: current.y + delta },
      };
      return changes[key] ? constrainPointToOrbit(changes[key]) : current;
    });
  }, []);

  const target = ORBIT_TARGETS[targetIndex];
  const targetCue = target ? text.cues[target.id] || target.cue : "";
  const cursor = (mode === "manual" ? manualPoint : cameraCursor) || ZERO_POINT;
  const runtime: GentleGameRuntime = {
    videoRef,
    status: headInputStatus,
    input: headInput,
    error: trackerError,
    loadProgress,
    calibrationProgress,
    comfortDirection: null,
    comfortReturning: false,
    comfortRetry: false,
    start: startTracker,
    stop: stopTracker,
    stage,
    mode,
    countdown,
    elapsed,
    manualPoint,
    point,
    cursor,
    startCamera,
    startManual,
    reset,
    complete,
    updateManualPoint: moveManualPoint,
    moveManualByKey,
  };
  const preview = <GameWorld game="firefly" step={3} cursor={{ x: -0.35, y: -0.2 }} />;

  if (stage === "intro") {
    return <GentleGameIntro runtime={runtime} eyebrow={text.eyebrow} title={text.introTitle} lead={text.introLead} preview={preview} previewLabel={text.arena} tone="firefly" />;
  }

  if (stage === "complete") {
    return <GentleGameResult runtime={runtime} eyebrow={text.completeEyebrow} title={text.completeTitle} body={text.completeBody(ORBIT_TARGETS.length, elapsed)} art={<GameWorld game="firefly" step={8} progress={1} />} tone="firefly" />;
  }

  return <GentleGameArena runtime={runtime} progress={(targetIndex + dwell) / ORBIT_TARGETS.length} arenaLabel={text.arena} playingTitle={targetCue} playingDetail={text.count(targetIndex + 1, ORBIT_TARGETS.length, elapsed)} className="firefly-game" controls={{ soundEnabled: audio.enabled, toggleSound: audio.toggle }}>
    <GameWorld game="firefly" cursor={cursor} step={targetIndex} dwell={dwell} active={stage === "playing"} onCourse={Boolean(point && target && targetReached(point, target))} />
  </GentleGameArena>;
}
